<!-- Layout follows option B and confirm step C on the "SG Edit links redesign" canvas: two equal
     columns of one fixed height so both tabs line up, 30px header rows, 44-48px list rows. -->
<ui-modal [open]="open" [width]="step() === 'confirm' ? '560px' : '920px'" (closed)="cancel()">
  <div class="flex max-h-[88vh] flex-col text-[13px]">

    <ng-container *ngIf="step() === 'edit'">
      <div class="flex flex-col gap-1.5 px-6 pt-5">
        <div class="flex items-baseline justify-between gap-4">
          <!-- A div, not an h2: the global h2 rule overrides the Tailwind size and the title came out huge. -->
          <div #editTitle tabindex="-1" role="heading" aria-level="2" class="text-xl font-semibold leading-7 text-[var(--primary-dark)] outline-none">
            Edit links<ng-container *ngIf="fieldName"> · {{ fieldName }}</ng-container>
          </div>
          <span *ngIf="manualChange() as m" class="whitespace-nowrap text-xs text-[var(--primary-dark)]/70">
            Manual since {{ m.at | date:'yyyy-MM-dd' }} by {{ m.userName || 'unknown user' }}
          </span>
        </div>
        <p class="m-0 text-[13px] text-[var(--primary-dark)]/70">
          <ng-container *ngIf="tab() !== 'rate'; else rateIntro">Saving marks this field as manually linked. The sync will not change it after that.</ng-container>
          <ng-template #rateIntro>How iGator run time becomes mm on this field. A rate set on the field overrides the farm's.</ng-template>
        </p>
        <div class="mt-3 flex gap-1 border-b border-[var(--app-border)]" role="tablist">
          <button type="button" role="tab" [attr.aria-selected]="tab() === 'fms'" (click)="tab.set('fms')"
                  class="-mb-px h-10 border-b-2 px-4 text-[13px]"
                  [ngClass]="tab() === 'fms' ? 'border-[var(--primary-dark)] font-semibold text-[var(--primary-dark)]' : 'border-transparent text-[var(--primary-dark)]/70'">
            FMS block · {{ currentBlock()?.blockNo || 'none' }}<ng-container *ngIf="fmsChanged()"> (changed)</ng-container>
          </button>
          <button type="button" role="tab" [attr.aria-selected]="tab() === 'igator'" (click)="tab.set('igator')"
                  class="-mb-px h-10 border-b-2 px-4 text-[13px]"
                  [ngClass]="tab() === 'igator' ? 'border-[var(--primary-dark)] font-semibold text-[var(--primary-dark)]' : 'border-transparent text-[var(--primary-dark)]/70'">
            iGator taps · {{ selectedTaps().size }}<ng-container *ngIf="tapsChanged()"> (changed)</ng-container>
          </button>
          <button type="button" role="tab" [attr.aria-selected]="tab() === 'rate'" (click)="tab.set('rate')"
                  class="-mb-px h-10 border-b-2 px-4 text-[13px]"
                  [ngClass]="tab() === 'rate' ? 'border-[var(--primary-dark)] font-semibold text-[var(--primary-dark)]' : 'border-transparent text-[var(--primary-dark)]/70'">
            Irrigation rate<ng-container *ngIf="rateInUse() as inUse"> · {{ rateLabel(inUse.mmPerHour) }} mm/h</ng-container><ng-container *ngIf="rateChanged()"> (changed)</ng-container>
          </button>
        </div>
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto px-6 py-[18px]">
        <p *ngIf="error()" class="mb-3 rounded-md border border-[var(--notification-error)] px-3 py-2 text-[var(--notification-error)]">{{ error() }}</p>
        <p *ngIf="loading()" class="py-10 text-center text-[var(--primary-dark)]/70">Loading FMS blocks and iGator taps...</p>

        <!-- FMS: one block per field, a block may serve several fields. -->
        <div *ngIf="!loading() && tab() === 'fms'" class="grid grid-cols-1 gap-5 md:h-[420px] md:grid-cols-2">
          <section class="flex min-h-0 flex-col gap-2.5">
            <div class="flex min-h-[34px] items-center justify-between">
              <span class="text-sm font-semibold">On this field</span>
              <span class="text-xs text-[var(--primary-dark)]/70">{{ currentBlock() ? '1 block' : 'No block' }}</span>
            </div>
            <div class="min-h-[120px] flex-1 overflow-y-auto rounded-lg border border-[var(--app-border)]">
              <div *ngIf="currentBlock() as b" class="flex min-h-12 items-center gap-2.5 px-3 py-1.5"
                   [ngClass]="{ 'bg-[var(--notification-warning)]/15': fmsChanged() }">
                <div class="flex min-w-0 flex-1 flex-col">
                  <span class="font-medium">Block {{ b.blockNo }} · {{ farmOf(b) }}</span>
                  <span class="text-[11px] text-[var(--primary-dark)]/70">
                    <ng-container *ngIf="fmsChanged()">Changed, not saved yet</ng-container>
                    <ng-container *ngIf="!fmsChanged() && context?.fms?.manual">Manual<ng-container *ngIf="context?.fms?.lastManualChange as m"> · {{ m.userName || 'unknown user' }} · {{ m.at | date:'yyyy-MM-dd' }}</ng-container></ng-container>
                    <ng-container *ngIf="!fmsChanged() && !context?.fms?.manual">Automatic</ng-container>
                    <ng-container *ngIf="otherFields(b) > 0"> · also on {{ otherFields(b) }} other field{{ otherFields(b) === 1 ? '' : 's' }}</ng-container>
                  </span>
                </div>
                <button type="button" (click)="fmsValue.set(NO_BLOCK)"
                        class="h-[30px] shrink-0 rounded-md border border-[var(--app-border)] bg-white px-2.5 text-xs">Remove</button>
              </div>
              <div *ngIf="!currentBlock()" class="flex h-full min-h-[120px] items-center justify-center px-6 text-center text-[var(--primary-dark)]/70">
                No FMS block. Pick one from the list.
              </div>
            </div>
            <p class="m-0 text-xs text-[var(--primary-dark)]/70">One block per field. A block may serve several fields.</p>
          </section>

          <section class="flex min-h-0 flex-col gap-2.5">
            <div class="flex min-h-[34px] items-center justify-between">
              <span class="text-sm font-semibold">Choose a block</span>
              <div class="w-48">
                <ui-dropdown [options]="fmsFarmOptions()" [value]="fmsFarm() ?? ''" (valueChange)="fmsFarm.set($event || null)"></ui-dropdown>
              </div>
            </div>
            <input type="search" placeholder="Search block or farm" aria-label="Search FMS blocks" [value]="fmsSearch()"
                   (input)="fmsSearch.set($any($event.target).value)"
                   class="box-border h-[38px] w-full rounded-md border border-[var(--app-border)] px-3 text-[13px] outline-none focus:border-[var(--primary-dark)]" />
            <div class="flex gap-1.5">
              <button *ngFor="let s of [{ id: 'unlinked', label: 'Not linked' }, { id: 'elsewhere', label: 'Linked elsewhere' }, { id: 'all', label: 'All' }]"
                      type="button" (click)="fmsStatus.set($any(s.id))" class="h-[30px] rounded-full border px-3 text-xs"
                      [ngClass]="fmsStatus() === s.id ? 'border-[var(--primary-dark)] bg-[var(--primary-dark)] text-white' : 'border-[var(--app-border)] bg-white'">{{ s.label }}</button>
            </div>
            <div class="min-h-[160px] flex-1 overflow-y-auto rounded-lg border border-[var(--app-border)] [scrollbar-gutter:stable]">
              <label *ngFor="let b of visibleBlocks()"
                     class="flex min-h-11 cursor-pointer items-center gap-2.5 border-b border-[var(--app-border)] px-3 py-1 last:border-b-0"
                     [ngClass]="{ 'bg-[var(--primary-dark)]/5': fmsValue() === blockKeyOf(b) }">
                <input type="radio" name="fms-block" class="size-4 shrink-0 accent-[var(--primary-dark)]"
                       [checked]="fmsValue() === blockKeyOf(b)" (change)="fmsValue.set(blockKeyOf(b))" />
                <span class="flex min-w-0 flex-1 flex-col">
                  <span class="font-medium">{{ b.blockNo }}</span>
                  <span class="truncate text-[11px] text-[var(--primary-dark)]/70">{{ farmOf(b) }}</span>
                </span>
                <span class="shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px]"
                      [ngClass]="otherFields(b) > 0 ? 'bg-[var(--notification-warning)]/20' : 'text-[var(--primary-dark)]/70'">
                  {{ otherFields(b) > 0 ? 'On ' + otherFields(b) + ' other field' + (otherFields(b) === 1 ? '' : 's') : 'Not linked' }}
                </span>
              </label>
              <div *ngIf="visibleBlocks().length === 0" class="px-3 py-3 text-[var(--primary-dark)]/70">No blocks match.</div>
            </div>
          </section>
        </div>

        <!-- iGator: any number of taps per field, but a tap sits on one field at a time. -->
        <div *ngIf="!loading() && tab() === 'igator'" class="grid grid-cols-1 gap-5 md:h-[420px] md:grid-cols-2">
          <section class="flex min-h-0 flex-col gap-2.5">
            <div class="flex min-h-[34px] items-center justify-between">
              <span class="text-sm font-semibold">On this field</span>
              <span class="text-xs text-[var(--primary-dark)]/70">{{ selectedTaps().size }} tap{{ selectedTaps().size === 1 ? '' : 's' }}</span>
            </div>
            <div class="min-h-[120px] flex-1 overflow-y-auto rounded-lg border border-[var(--app-border)] [scrollbar-gutter:stable]">
              <div *ngFor="let tap of linkedTaps()" class="flex min-h-12 items-center gap-2.5 border-b border-[var(--app-border)] px-3 py-1 last:border-b-0"
                   [ngClass]="{ 'bg-[var(--notification-warning)]/15': candidateFor(tap) && isOnOtherField(candidateFor(tap)!) }">
                <div class="flex min-w-0 flex-1 flex-col">
                  <span class="font-medium">{{ tap }}</span>
                  <span class="text-[11px] text-[var(--primary-dark)]/70" [ngSwitch]="tapOrigin(tap).kind">
                    <ng-container *ngSwitchCase="'new'">
                      <ng-container *ngIf="candidateFor(tap) as c">{{ isOnOtherField(c) ? 'Will move from ' + fieldLabel(c) : 'Added, not saved yet' }}</ng-container>
                    </ng-container>
                    <ng-container *ngSwitchDefault>
                      <ng-container *ngIf="$any(tapOrigin(tap)).detail as d; else linkedOnly">
                        <ng-container *ngIf="d.source === 'MANUAL'">Manual<ng-container *ngIf="d.linkedBy"> · {{ d.linkedBy.userName || 'unknown user' }} · {{ d.linkedBy.at | date:'yyyy-MM-dd' }}</ng-container></ng-container>
                        <ng-container *ngIf="d.source === 'AUTO'">Automatic</ng-container>
                      </ng-container>
                      <ng-template #linkedOnly>Linked</ng-template>
                    </ng-container>
                  </span>
                </div>
                <button type="button" (click)="toggleTap(tap)" [attr.aria-label]="(tapOrigin(tap).kind === 'new' ? 'Undo adding ' : 'Remove ') + tap"
                        class="h-[30px] shrink-0 rounded-md border border-[var(--app-border)] bg-white px-2.5 text-xs">
                  {{ tapOrigin(tap).kind === 'new' ? 'Undo' : 'Remove' }}
                </button>
              </div>
              <div *ngIf="selectedTaps().size === 0" class="flex h-full min-h-[120px] items-center justify-center px-6 text-center text-[var(--primary-dark)]/70">
                No taps. Saving leaves this field without iGator data.
              </div>
            </div>
            <p class="m-0 text-xs text-[var(--primary-dark)]/70">Past irrigation records move with a tap that comes from another field.</p>
          </section>

          <section class="flex min-h-0 flex-col gap-2.5">
            <div class="flex min-h-[34px] items-center justify-between">
              <span class="text-sm font-semibold">Add taps</span>
              <div class="w-48">
                <ui-dropdown [options]="tapFarmOptions()" [value]="tapFarmFilter() ?? ''" (valueChange)="tapFarmFilter.set($event || null)"></ui-dropdown>
              </div>
            </div>
            <input type="search" placeholder="Search taps or field names" aria-label="Search iGator taps" [value]="tapSearch()"
                   (input)="tapSearch.set($any($event.target).value)"
                   class="box-border h-[38px] w-full rounded-md border border-[var(--app-border)] px-3 text-[13px] outline-none focus:border-[var(--primary-dark)]" />
            <div class="flex gap-1.5">
              <button *ngFor="let s of [{ id: 'unassigned', label: 'Unassigned' }, { id: 'other', label: 'On other fields' }, { id: 'all', label: 'All' }]"
                      type="button" (click)="tapStatus.set($any(s.id))" class="h-[30px] rounded-full border px-3 text-xs"
                      [ngClass]="tapStatus() === s.id ? 'border-[var(--primary-dark)] bg-[var(--primary-dark)] text-white' : 'border-[var(--app-border)] bg-white'">{{ s.label }}</button>
            </div>
            <div class="min-h-[160px] flex-1 overflow-y-auto rounded-lg border border-[var(--app-border)] [scrollbar-gutter:stable]">
              <ng-container *ngFor="let g of availableTapGroups()">
                <div class="sticky top-0 border-b border-[var(--app-border)] bg-[var(--app-base)] px-3 py-1.5 text-[11px] font-semibold text-[var(--primary-dark)]/70">{{ g.farm }}</div>
                <div *ngFor="let c of g.taps" class="flex min-h-11 items-center gap-2.5 border-b border-[var(--app-border)] px-3 py-1">
                  <span class="flex min-w-0 flex-1 flex-col">
                    <span class="font-medium">{{ c.tap }}</span>
                    <span class="truncate text-[11px] text-[var(--primary-dark)]/70">{{ isOnOtherField(c) ? 'On ' + fieldLabel(c) : 'Unassigned' }}</span>
                  </span>
                  <button type="button" (click)="toggleTap(c.tap)" [attr.aria-label]="'Add ' + c.tap"
                          class="h-[30px] shrink-0 rounded-md bg-[var(--primary-dark)]/10 px-3 text-xs font-medium text-[var(--primary-dark)]">Add</button>
                </div>
              </ng-container>
              <div *ngIf="availableTapGroups().length === 0" class="px-3 py-3 text-[var(--primary-dark)]/70">No taps match.</div>
            </div>
          </section>
        </div>

        <!-- Irrigation rate: the field's own rate overrides its farm's; with neither, the default. -->
        <div *ngIf="!loading() && tab() === 'rate'" class="grid grid-cols-1 gap-5 md:min-h-[420px] md:grid-cols-2">
          <p *ngIf="rateUnavailable()" class="m-0 rounded-lg border border-[var(--app-border)] px-3 py-3 text-[var(--primary-dark)]/70 md:col-span-2">
            The irrigation rate could not be loaded. Links can still be edited and saved.
          </p>
          <ng-container *ngIf="rate() as r">
            <section class="flex min-h-0 flex-col gap-2.5">
              <div class="flex min-h-[34px] items-center justify-between">
                <span class="text-sm font-semibold">This field</span>
                <span class="text-xs text-[var(--primary-dark)]/70">Turns iGator run time into mm</span>
              </div>
              <div *ngIf="rateInUse() as inUse" class="flex flex-col gap-1.5 rounded-lg border border-[var(--app-border)] p-4"
                   [ngClass]="{ 'bg-[var(--notification-warning)]/15': rateChanged() }">
                <span class="text-xs text-[var(--primary-dark)]/70">{{ rateChanged() ? 'Rate in use after saving' : 'Rate in use' }}</span>
                <div class="flex flex-wrap items-baseline gap-2.5">
                  <span class="text-[28px] font-semibold leading-9 text-[var(--primary-dark)]">{{ rateLabel(inUse.mmPerHour) }} mm/h</span>
                  <span class="rounded-full px-2 py-0.5 text-[11px]"
                        [ngClass]="inUse.source === 'DEFAULT' ? 'bg-[var(--app-base)]' : 'bg-[var(--primary-dark)]/10 text-[var(--primary-dark)]'"
                        [ngSwitch]="inUse.source">
                    <ng-container *ngSwitchCase="'FIELD'">Set on this field</ng-container>
                    <ng-container *ngSwitchCase="'FARM'">From farm {{ r.farmName || r.farmId }}</ng-container>
                    <ng-container *ngSwitchDefault>Default, no farm rate set</ng-container>
                  </span>
                </div>
                <span *ngIf="exampleMm() !== null" class="text-xs text-[var(--primary-dark)]/70">
                  {{ EXAMPLE_MINUTES }} min of irrigation puts {{ mmLabel(exampleMm()!) }} mm on this field.
                </span>
              </div>
              <fieldset class="m-0 rounded-lg border border-[var(--app-border)] p-0">
                <legend class="sr-only">Rate for this field</legend>
                <label class="flex min-h-12 cursor-pointer items-center gap-2.5 border-b border-[var(--app-border)] px-3">
                  <input type="radio" name="rate-mode" class="size-4 shrink-0 accent-[var(--primary-dark)]"
                         [checked]="fieldRateMode() === 'farm'" (change)="fieldRateMode.set('farm')" />
                  <span class="flex flex-col">
                    <span class="font-medium">Use the farm rate</span>
                    <span class="text-[11px] text-[var(--primary-dark)]/70">
                      <ng-container *ngIf="r.farmMmPerHour != null; else noFarmRate">{{ rateLabel(r.farmMmPerHour) }} mm/h, follows the farm if it changes</ng-container>
                      <ng-template #noFarmRate>The farm has none yet, so the default {{ rateLabel(r.defaultMmPerHour) }} mm/h</ng-template>
                    </span>
                  </span>
                </label>
                <label class="flex min-h-12 cursor-pointer items-center gap-2.5 px-3 py-1"
                       [ngClass]="{ 'bg-[var(--notification-warning)]/15': fieldRateChanged() && fieldRateMode() === 'field' }">
                  <input type="radio" name="rate-mode" class="size-4 shrink-0 accent-[var(--primary-dark)]"
                         [checked]="fieldRateMode() === 'field'" (change)="fieldRateMode.set('field')" />
                  <span class="flex min-w-0 flex-1 flex-col">
                    <span class="font-medium">Set a rate for this field only</span>
                    <span class="text-[11px] text-[var(--primary-dark)]/70">
                      <ng-container *ngIf="fieldRateChanged(); else fieldRateNote">Changed, not saved yet</ng-container>
                      <ng-template #fieldRateNote>
                        <ng-container *ngIf="r.fieldChange as c; else overridesNote">Set by {{ c.userName || 'unknown user' }} · {{ c.at | date:'yyyy-MM-dd' }}</ng-container>
                        <ng-template #overridesNote>Overrides the farm rate on this field</ng-template>
                      </ng-template>
                    </span>
                  </span>
                  <span class="flex shrink-0 items-center gap-1.5">
                    <input type="text" inputmode="decimal" aria-label="Rate for this field, mm per hour"
                           [placeholder]="rateLabel(r.farmMmPerHour ?? r.defaultMmPerHour)" [value]="fieldRateText()"
                           (input)="fieldRateText.set($any($event.target).value)" (focus)="fieldRateMode.set('field')"
                           class="box-border h-[34px] w-[72px] rounded-md border border-[var(--app-border)] px-2.5 text-right text-[13px] outline-none focus:border-[var(--primary-dark)]"
                           [ngClass]="{ 'text-[var(--primary-dark)]/50': fieldRateMode() === 'farm' }" />
                    <span class="text-xs text-[var(--primary-dark)]/70">mm/h</span>
                  </span>
                </label>
              </fieldset>
              <p class="m-0 text-xs text-[var(--primary-dark)]/70">The rate applies to all of this field's irrigation, past and future.</p>
            </section>

            <section *ngIf="r.farmId != null; else noFarm" class="flex min-h-0 flex-col gap-2.5">
              <div class="flex min-h-[34px] items-center justify-between gap-3">
                <span class="truncate text-sm font-semibold">Farm rate · {{ r.farmName || 'farm ' + r.farmId }}</span>
                <span class="shrink-0 text-xs text-[var(--primary-dark)]/70">{{ r.farmFieldCount }} field{{ r.farmFieldCount === 1 ? '' : 's' }}</span>
              </div>
              <div class="flex flex-col gap-3 rounded-lg border border-[var(--app-border)] p-4"
                   [ngClass]="{ 'border-dashed': r.farmMmPerHour == null && !farmRateChanged() }">
                <span *ngIf="r.farmMmPerHour == null" class="text-[13px]">
                  No rate set for this farm. Its fields use the default of {{ rateLabel(r.defaultMmPerHour) }} mm/h.
                </span>
                <label class="flex flex-col gap-1.5">
                  <span class="text-[13px] font-medium">{{ r.farmMmPerHour == null ? 'Set a rate for every field on this farm' : 'Rate for every field on this farm' }}</span>
                  <span class="flex items-center gap-1.5">
                    <input type="text" inputmode="decimal" aria-label="Farm rate, mm per hour" placeholder="e.g. 8,77"
                           [value]="farmRateText()" (input)="farmRateText.set($any($event.target).value)"
                           class="box-border h-[38px] w-24 rounded-md border border-[var(--app-border)] px-2.5 text-right text-sm outline-none focus:border-[var(--primary-dark)]"
                           [ngClass]="{ 'bg-[var(--notification-warning)]/15': farmRateChanged() }" />
                    <span class="text-[13px] text-[var(--primary-dark)]/70">mm/h</span>
                  </span>
                </label>
                <div class="flex flex-col gap-1 text-xs text-[var(--primary-dark)]/70">
                  <span>Used by {{ r.farmFieldCount - r.farmOverrideCount }} of {{ r.farmFieldCount }} fields. {{ r.farmOverrideCount }} have their own rate.</span>
                  <span *ngIf="r.farmChange as c">Last changed {{ c.at | date:'yyyy-MM-dd' }} by {{ c.userName || 'unknown user' }}</span>
                </div>
              </div>
              <div class="rounded-lg bg-[var(--app-base)] px-3 py-2.5 text-xs">
                Changing the farm rate recalculates past and future mm on every field that uses it. You confirm before it saves.
              </div>
              <p class="m-0 text-xs text-[var(--primary-dark)]/70">Accepts 8,77 or 8.77, above 0 and at most 100. Leave it empty to remove the farm rate.</p>
            </section>
            <ng-template #noFarm>
              <section class="flex items-center justify-center rounded-lg border border-[var(--app-border)] px-6 text-center text-[var(--primary-dark)]/70">
                This field is not on a farm, so only its own rate or the default applies.
              </section>
            </ng-template>

            <p *ngIf="rateError()" class="m-0 text-xs text-[var(--notification-error)] md:col-span-2">{{ rateError() }}</p>
          </ng-container>
        </div>

        <details *ngIf="!loading() && historyCount()" class="mt-4">
          <summary class="cursor-pointer text-xs text-[var(--primary-dark)]/70">Change history ({{ historyCount() }})</summary>
          <ul class="mt-2 max-h-40 overflow-y-auto text-xs">
            <li *ngFor="let h of history()" class="py-0.5">
              {{ h.at | date:'yyyy-MM-dd HH:mm' }}, {{ h.userName || 'unknown user' }}:
              <ng-container [ngSwitch]="h.action">
                <span *ngSwitchCase="'SET_FMS'">set FMS block {{ h.value }}</span>
                <span *ngSwitchCase="'CLEAR_FMS'">removed the FMS block</span>
                <span *ngSwitchCase="'ADD_TAP'">added tap {{ h.value }}</span>
                <span *ngSwitchCase="'MOVE_TAP'">moved tap {{ h.value }} here from field {{ h.fromFieldId }}</span>
                <span *ngSwitchCase="'REMOVE_TAP'">removed tap {{ h.value }}</span>
              </ng-container>
            </li>
            <li *ngFor="let h of rateHistory()" class="py-0.5">
              {{ h.at | date:'yyyy-MM-dd HH:mm' }}, {{ h.userName || 'unknown user' }}: {{ describeRateChange(h) }}
            </li>
          </ul>
        </details>
      </div>
    </ng-container>

    <ng-container *ngIf="step() === 'confirm'">
     <ng-container *ngIf="tapsChanged() && moveRows().length">
      <div class="flex items-start gap-3.5 border-b border-[var(--app-border)] px-6 pb-4 pt-5">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"
             stroke-linejoin="round" aria-hidden="true" class="mt-0.5 shrink-0">
          <path d="M12 3 2.5 20h19L12 3z"></path><path d="M12 10v4"></path><path d="M12 17h.01"></path>
        </svg>
        <div class="flex flex-col gap-1.5">
          <div role="heading" aria-level="2" class="text-lg font-semibold leading-6">
            Move {{ moveRows().length }} tap{{ moveRows().length === 1 ? '' : 's' }} to {{ fieldName || 'this field' }}?
          </div>
          <p class="m-0 text-[13px] text-[var(--primary-dark)]/70">
            {{ moveRows().length === 1 ? 'This tap is' : 'These taps are' }} linked to another field. Saving moves
            {{ moveRows().length === 1 ? 'it' : 'them' }} here, together with past irrigation records, so that field will no longer show that irrigation.
          </p>
        </div>
      </div>
      <div class="max-h-[50vh] overflow-y-auto px-6 py-4">
        <div class="grid grid-cols-3 gap-3 pb-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--primary-dark)]/70">
          <span>Tap</span><span>From field</span><span>That field keeps</span>
        </div>
        <div *ngFor="let r of moveRows()" class="grid min-h-11 grid-cols-3 items-center gap-3 border-t border-[var(--app-border)]">
          <span class="font-medium">{{ r.tap }}</span>
          <span>{{ r.fromFieldName || 'field ' + r.fromFieldId }}</span>
          <span *ngIf="r.remaining > 0" class="text-[var(--primary-dark)]/70">{{ r.remaining }} other tap{{ r.remaining === 1 ? '' : 's' }}</span>
          <span *ngIf="r.remaining === 0"><span class="rounded-full bg-[var(--notification-warning)]/25 px-2 py-0.5 font-medium">No taps left</span></span>
        </div>
      </div>
      <div class="mx-6 mb-4 rounded-lg bg-[var(--app-base)] px-3 py-2.5 text-xs">
        Moved taps are marked manual, so the sync will not move them back.
      </div>
     </ng-container>

     <!-- A farm rate change reaches every field on the farm rate, past irrigation included. -->
     <ng-container *ngIf="farmChange() as fc">
      <div class="flex items-start gap-3.5 border-b border-[var(--app-border)] px-6 pb-4 pt-5">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"
             stroke-linejoin="round" aria-hidden="true" class="mt-0.5 shrink-0">
          <path d="M12 3 2.5 20h19L12 3z"></path><path d="M12 10v4"></path><path d="M12 17h.01"></path>
        </svg>
        <div class="flex flex-col gap-1.5">
          <div role="heading" aria-level="2" class="text-lg font-semibold leading-6">
            {{ fc.cleared ? 'Remove the farm rate for ' : 'Change the farm rate for ' }}{{ fc.farmName }}?
          </div>
          <p class="m-0 text-[13px] text-[var(--primary-dark)]/70">
            From {{ rateLabel(fc.from) }} to {{ rateLabel(fc.to) }} mm/h<ng-container *ngIf="fc.cleared">, the default</ng-container>.
            Every field on the farm rate gets the new rate for all its irrigation, past and future.
          </p>
        </div>
      </div>
      <div class="px-6 py-4">
        <div class="grid grid-cols-[1.4fr_0.6fr_1fr] gap-3 pb-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--primary-dark)]/70">
          <span>Fields</span><span>Count</span><span>Irrigation mm</span>
        </div>
        <div class="grid min-h-11 grid-cols-[1.4fr_0.6fr_1fr] items-center gap-3 border-t border-[var(--app-border)]">
          <span class="font-medium">On the farm rate</span>
          <span>{{ fc.onFarmRate }}</span>
          <span><span class="rounded-full bg-[var(--notification-warning)]/25 px-2 py-0.5 font-medium">{{ fc.change }}</span></span>
        </div>
        <div class="grid min-h-11 grid-cols-[1.4fr_0.6fr_1fr] items-center gap-3 border-t border-[var(--app-border)]">
          <span class="font-medium">With their own rate</span>
          <span>{{ fc.ownRate }}</span>
          <span class="text-[var(--primary-dark)]/70">Not affected</span>
        </div>
        <div class="grid min-h-11 grid-cols-[1.4fr_0.6fr_1fr] items-center gap-3 border-t border-[var(--app-border)]">
          <span class="font-medium">Example: {{ EXAMPLE_MINUTES }} min run</span>
          <span></span>
          <span>{{ mmLabel(fc.exampleFrom) }} to {{ mmLabel(fc.exampleTo) }} mm</span>
        </div>
      </div>
      <div class="mx-6 mb-4 rounded-lg bg-[var(--app-base)] px-3 py-2.5 text-xs">
        Past charts and totals for these {{ fc.onFarmRate }} fields will show the new figures. The old rate stays in the change history.
      </div>
     </ng-container>
    </ng-container>

    <div class="flex items-center gap-2.5 border-t border-[var(--app-border)] px-6 py-3.5">
      <span class="flex-1 text-xs">
        <ng-container *ngIf="step() === 'edit' && tapsToMove().length">
          {{ tapsToMove().length }} tap{{ tapsToMove().length === 1 ? '' : 's' }} will be taken from another field
        </ng-container>
        <ng-container *ngIf="step() === 'edit' && !tapsToMove().length && rateChanged() && !rateError()">
          The new rate applies to past irrigation too
        </ng-container>
      </span>
      <button type="button" class="h-[38px] rounded-md border border-[var(--app-border)] bg-white px-4" (click)="cancel()" [disabled]="saving()">
        {{ step() === 'confirm' ? 'Back' : 'Cancel' }}
      </button>
      <button type="button" class="h-[38px] rounded-md bg-[var(--primary-dark)] px-[18px] font-medium text-white disabled:opacity-50"
              (click)="save()" [disabled]="saving() || loading()">
        {{ saving() ? 'Saving...' : step() === 'confirm' ? confirmLabel() : 'Save' }}
      </button>
    </div>
  </div>
</ui-modal>
